import { mergeRegister } from '@lexical/utils';
import {
  $getSelection,
  $isElementNode,
  $isNodeSelection,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_LOW,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  type LexicalEditor,
  type LexicalNode,
} from 'lexical';

type IsAtomic = (node: LexicalNode | null | undefined) => boolean;

/** Removes an atomic node the caret is directly beside (Backspace before, Delete after) or that is selected. */
function $removeIfAdjacent(isAtomic: IsAtomic, isBackspace: boolean): boolean {
  const selection = $getSelection();

  // If the node itself is selected, remove it.
  if ($isNodeSelection(selection)) {
    const atomic = selection.getNodes().find(isAtomic);
    if (atomic) {
      atomic.remove();
      return true;
    }
    return false;
  }

  if (!$isRangeSelection(selection) || !selection.isCollapsed()) return false;

  const { anchor } = selection;
  const anchorNode = anchor.getNode();

  // Case A: caret inside a TextNode
  if ($isTextNode(anchorNode)) {
    const offset = anchor.offset;

    if (isBackspace) {
      // At start of text -> check previous sibling
      if (offset === 0) {
        const prev = anchorNode.getPreviousSibling();
        if (prev && isAtomic(prev)) {
          prev.remove();
          return true;
        }
      }
    } else {
      // At end of text -> check next sibling
      if (offset === anchorNode.getTextContentSize()) {
        const next = anchorNode.getNextSibling();
        if (next && isAtomic(next)) {
          next.remove();
          return true;
        }
      }
    }
    return false;
  }

  // Case B (IMPORTANT): caret anchored on an ElementNode between children
  // anchor.offset is the child index the caret is at.
  if ($isElementNode(anchorNode)) {
    const offset = anchor.offset;

    const maybeNode = isBackspace
      ? anchorNode.getChildAtIndex(offset - 1) // child before caret
      : anchorNode.getChildAtIndex(offset); // child after caret

    if (maybeNode && isAtomic(maybeNode)) {
      maybeNode.remove();
      return true;
    }

    return false;
  }

  return false;
}

/**
 * Backspace / Delete remove an inline node (a dynamic-data chip, a mention) as a whole, never a character of it.
 * - `isAtomic` picks the nodes this applies to.
 */
export const registerAtomicDeletion = (editor: LexicalEditor, isAtomic: IsAtomic): (() => void) =>
  mergeRegister(
    editor.registerCommand(KEY_BACKSPACE_COMMAND, () => $removeIfAdjacent(isAtomic, true), COMMAND_PRIORITY_LOW),
    editor.registerCommand(KEY_DELETE_COMMAND, () => $removeIfAdjacent(isAtomic, false), COMMAND_PRIORITY_LOW),
  );
